"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAction, useMutation } from "convex/react";
import { withUser } from "@/hooks/with-user";
import { api } from "@/convex/_generated/api";
import { RolloutCredentialResponse } from "@/models/RolloutCredentialResponse";
import { useCrmStatus, type CrmStatus } from "./use-crm-status";

export interface RolloutCredential {
  id: string;
  appKey: string;
}

export interface ConnectorInputField {
  type: string;
  key: string;
  label: string;
  placeholder?: string;
  infoHTML?: string;
}

export interface ConnectorAuth {
  type: string;
  userInputsSchema: ConnectorInputField[];
}

export interface ConnectorInfo {
  name: string;
  auth: ConnectorAuth;
  authByPlan?: Record<string, ConnectorAuth>;
}

export class RolloutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RolloutError";
  }
}

const ROLLOUT_URLS = {
  CONNECTORS_GET_URL: "https://universal.rollout.com/api/connectors",
  CREDENTIALS_BASE_URL: "https://universal.rollout.com/api/credentials",
};

const POLL_INTERVAL_MS = 30_000;

/**
 * Backwards-compat alias for the metadata shape consumers used to read off
 * the old `crmMetadata` object. New code should prefer `useCrmStatus()`
 * directly, which returns a richer `CrmStatus`.
 */
export type CrmMetadata = {
  provider: "fub" | "rollout";
  appKey?: string;
  credentialId?: string;
  isConnected: boolean;
  isFub: boolean;
  isRollout: boolean;
  isSyncing: boolean;
  lastSyncedAt?: number;
};

/**
 * Heavy hook for the CRM integration settings page: token fetching,
 * connector listing, credential CRUD, and 30-second polling for the
 * Rollout data summary + tags.
 *
 * For pages that only need to know "is the CRM connected / what name
 * to display", import `useCrmStatus` from `./use-crm-status` instead —
 * it skips all of the polling and credential machinery.
 */
export const useRollout = () => {
  const { team } = withUser();
  const crmStatus: CrmStatus = useCrmStatus();

  const connectRollout = useMutation(api.rollout.mutations.connectRollout);
  const connectCrmDirect = useMutation(api.rollout.mutations.connectCrmDirect);
  const logCredentialResponse = useMutation(api.rollout.connectionLog.logCredentialResponse);
  const clearCrm = useMutation(api.admin.teams.clearCrm);
  const startActivationSync = useAction(api.activationSync.actions.startActivationSync);

  const getDataSummaryAction = useAction(api.rolloutPostgres.publicQueries.getDataSummary);
  const getRolloutTagsAction = useAction(api.rolloutPostgres.publicQueries.getRolloutTags);

  const [connectors, setConnectors] = useState<Record<string, ConnectorInfo> | null>(null);
  const [tags, setTags] = useState<Array<{ tag: string; name: string; memberCount: number }>>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [dataSummary, setDataSummary] = useState<{
    teamMemberCount: number;
    contactCount: number;
    tagCount: number;
    lastSyncedAt: number;
  } | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Backwards-compat shape: synthesise the old `crmMetadata` object from
  // `crmStatus`. Computed lazily so consumers that destructure it keep working.
  const crmMetadata = useMemo<CrmMetadata | null>(() => {
    if (!crmStatus.isLoaded || !crmStatus.provider) return null;
    return {
      provider: crmStatus.provider,
      appKey: crmStatus.appKey ?? undefined,
      credentialId: crmStatus.credentialId ?? undefined,
      isConnected: crmStatus.isConnected,
      isFub: crmStatus.isFub,
      isRollout: crmStatus.isRollout,
      isSyncing: crmStatus.isSyncing,
      lastSyncedAt: crmStatus.lastSyncedAt ?? undefined,
    };
  }, [crmStatus]);

  const fetchToken = useCallback(async (): Promise<string> => {
    const response = await fetch("/api/rollout/token", { method: "POST" });
    if (!response.ok) {
      throw new RolloutError("Failed to get Rollout token");
    }
    const data = (await response.json()) as { token: string };
    return data.token;
  }, []);

  const fetchConnectors = useCallback(async () => {
    const token = await fetchToken();
    if (!token) return;
    const response = await fetch(ROLLOUT_URLS.CONNECTORS_GET_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    setConnectors(await response.json());
  }, [fetchToken]);

  const getTags = useCallback(async () => {
    if (!crmStatus.isRollout || !crmStatus.isConnected) return;
    setIsLoadingTags(true);
    try {
      const data = await getRolloutTagsAction({});
      setTags(data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    } finally {
      setIsLoadingTags(false);
    }
  }, [crmStatus.isRollout, crmStatus.isConnected, getRolloutTagsAction]);

  const refreshStatus = useCallback(async () => {
    if (!crmStatus.isRollout || !crmStatus.isConnected) return;
    setIsLoadingData(true);
    try {
      const data = await getDataSummaryAction({});
      setDataSummary(data);
    } catch (error) {
      console.error("Error fetching data summary:", error);
    } finally {
      setIsLoadingData(false);
    }
  }, [crmStatus.isRollout, crmStatus.isConnected, getDataSummaryAction]);

  // Poll Rollout database every 30 seconds. No-op when not connected via Rollout.
  useEffect(() => {
    if (!crmStatus.isRollout || !crmStatus.isConnected) return;
    const interval = setInterval(refreshStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [crmStatus.isRollout, crmStatus.isConnected, refreshStatus]);

  // Initial fetch — connectors, summary, tags — runs once when status resolves.
  useEffect(() => {
    Promise.all([fetchConnectors(), refreshStatus(), getTags()]).catch((error) => {
      console.error("Error fetching initial rollout data:", error);
    });
  }, [fetchConnectors, refreshStatus, getTags]);

  const getRolloutCredentialInfo = async (): Promise<{ data?: any } | null> => {
    const credentialId = team.crm?.credentials?.credentialId;
    if (!credentialId) {
      return null;
    }

    const rolloutCredentialInfoUrl = `${ROLLOUT_URLS.CREDENTIALS_BASE_URL}/${credentialId}`;
    try {
      const token = await fetchToken();
      const response = await fetch(rolloutCredentialInfoUrl, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          entitiesToSync: {
            people: true,
            tags: true,
            users: true,
            lists: true,
          },
        }),
      });
      if (!response.ok) {
        const error = (await response.json()) as { errorMessage: string };
        if (response.status === 400 && error.errorMessage === "Credential not found") {
          await clearCrm({});
          return null;
        }
        console.warn("Failed to get credential info");
        return null;
      }
      const data = (await response.json()) as any;
      return data;
    } catch (error: unknown) {
      console.error("Error getting credential info", error);
      return null;
    }
  };

  const deleteCredential = async (): Promise<{ success: boolean; errorMessage?: string }> => {
    const credentialId = team.crm?.credentials?.credentialId;

    // Best-effort: tell Rollout to delete the remote credential. If it's already
    // gone (404 / NOT_FOUND) or the call fails entirely, we still clear local
    // state — the user asked to disconnect, and a stale local CRM is worse
    // than a dangling remote credential.
    if (credentialId) {
      try {
        const token = await fetchToken();
        const response = await fetch(`${ROLLOUT_URLS.CREDENTIALS_BASE_URL}/${credentialId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok && response.status !== 404) {
          console.warn("[useRollout] Rollout DELETE failed", response.status, await response.text());
        }
      } catch (err) {
        console.warn("[useRollout] Rollout DELETE threw", err);
      }
    }

    try {
      await clearCrm({});
      return { success: true };
    } catch (err) {
      console.error("[useRollout] clearCrm failed", err);
      return {
        success: false,
        errorMessage: "Failed to clear CRM from your account. Please try again.",
      };
    }
  };

  const createCredential = async (
    appKey: string,
    userInputs: Record<string, string>,
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    // Rollout connectors don't all key their auth field as `apiKey` (some
    // use `token`, `key`, etc.), so search every userInputs value for the
    // first reasonable-looking string instead of relying on a fixed name.
    // Hoisted to outer scope so the Rollout-failure fallback can use it too.
    const candidateApiKey = Object.entries(userInputs)
      .find(([, v]) => typeof v === "string" && v.trim().length > 10)?.[1]
      ?.trim();

    // Fires activationSync for the supported CRMs. Kept as a local helper so
    // both the normal Rollout path and the direct-connect fallback invoke it
    // identically. Keep in sync with `ACTIVATION_SYNC_APP_KEYS` in
    // `app/v2/activate/_components/contact-sync-status.tsx` and
    // `APP_KEY_TO_PROVIDER` in `convex/activationSync/actions.ts`.
    const activationSyncAppKeys = new Set(["lofty", "follow-up-boss", "boldtrail"]);
    const triggerActivationSyncIfSupported = () => {
      if (!activationSyncAppKeys.has(appKey) || !candidateApiKey) return;
      void (async () => {
        try {
          await startActivationSync({ apiKey: candidateApiKey });
        } catch (err) {
          console.error("[useRollout] startActivationSync failed:", err);
        }
      })();
    };

    try {
      const token = await fetchToken();
      const response = await fetch(ROLLOUT_URLS.CREDENTIALS_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          appKey,
          userInputs,
          plan: "standard",
        }),
      });
      if (!response.ok) {
        const error = (await response.json()) as { errors: { message: string }[] };
        // Rollout's BoldTrail connector currently rejects valid kvCORE tokens
        // with "AssertFetchShapeError: Unexpected Fetch Shape". Our activation
        // sync hits kvCORE directly and doesn't need Rollout — fall back to a
        // direct connection that persists the key ourselves, then fire
        // activationSync. Any other CRM falls through to the normal error.
        if (appKey === "boldtrail" && candidateApiKey) {
          console.warn(
            "[useRollout] Rollout rejected BoldTrail credential — falling back to direct connect:",
            error?.errors?.[0]?.message,
          );
          const direct = await connectCrmDirect({ appKey, apiKey: candidateApiKey });
          if (direct.success) {
            triggerActivationSyncIfSupported();
            return { success: true };
          }
        }
        if (error && "errors" in error) {
          throw new RolloutError(RolloutCredentialResponse.getFriendlyErrorMessage(error));
        }
        throw new RolloutError("Something went wrong! We've been notified and will reach out to you shortly.");
      }
      const credential = (await response.json()) as { id: string };

      // Log the full credential response
      await logCredentialResponse({
        credentialId: credential.id,
        appKey: appKey,
        rawResponse: credential,
      });

      // Complete the connection flow. Pass the apiKey through so it gets
      // persisted to `team.crm.credentials.apiKey` — ContactSyncStatus's
      // fallback trigger and any future retry path re-invoke
      // startActivationSync without re-prompting the user.
      const result = await connectRollout({
        credentialId: credential.id,
        appKey: appKey,
        ...(candidateApiKey ? { apiKey: candidateApiKey } : {}),
      });

      // Activation sync: for Lofty / FUB-via-Rollout / BoldTrail we fetch
      // contacts directly from the CRM's API now so onboarding enrichment
      // can proceed without waiting for Rollout's background sync. Rollout
      // keeps syncing in the background for future operations.
      if (result.success) triggerActivationSyncIfSupported();

      return result;
    } catch (error: unknown) {
      if (error instanceof RolloutError) {
        return { success: false, errorMessage: error.message };
      }
      return {
        success: false,
        errorMessage: "Something went wrong! We've been notified and will reach out to you shortly.",
      };
    }
  };

  /**
   * Used with the @rollout/link-react CredentialForm to handle the credential added event.
   */
  const handleCredentialAdded = useCallback(
    async (credential: RolloutCredential) => {
      try {
        const result = await connectRollout({
          credentialId: credential.id,
          appKey: credential.appKey,
        });

        if (result.success) {
          return { success: true };
        } else {
          return { success: false, errorMessage: "Failed to connect. Please try again." };
        }
      } catch (err) {
        console.error("Error connecting Rollout:", err);
        return { success: false, errorMessage: "An error occurred while connecting." };
      }
    },
    [connectRollout],
  );

  /**
   * Used with the @rollout/link-react CredentialForm to handle the credential deleted event.
   */
  const handleCredentialDeleted = useCallback(async () => {
    try {
      const result = await deleteCredential({});

      if (result.success) {
        return { success: true };
      } else {
        return { success: false, errorMessage: "Failed to disconnect. Please try again." };
      }
    } catch (err) {
      console.error("Error disconnecting Rollout:", err);
      return { success: false, errorMessage: "An error occurred while disconnecting." };
    }
  }, [deleteCredential]);

  return {
    // Status (forwarded from useCrmStatus)
    crmMetadata,
    crmConfig: crmStatus.crmConfig ?? null,
    crmFriendlyName: crmStatus.crmFriendlyName,

    // Rollout-only data
    tags,
    connectors,
    dataSummary,
    isLoadingData,
    isLoadingTags,

    // Actions
    fetchToken,
    refreshStatus,
    createCredential,
    deleteCredential,
    getRolloutCredentialInfo,

    // Handlers for the @rollout/link-react CredentialForm
    handleCredentialAdded,
    handleCredentialDeleted,
  };
};
